// CTMS embeddable booking widget.
//
// Customer sites embed via:
//   <div id="ctms-booking"></div>
//   <script src="https://<tenant>.booking.cheeptech.com.au/widget.js"
//           data-mount="#ctms-booking"
//           async></script>
//
// Or as a popup, with no layout slot needed:
//   <script src="https://<tenant>.booking.cheeptech.com.au/widget.js"
//           data-modal="1"
//           data-trigger=".book-trial"
//           data-service="<serviceId>"
//           data-party-size="1"
//           async></script>
// In modal mode the widget also exposes window.ctmsOpenBooking().
//
// The widget infers the tenant from its own origin (subdomain), fetches the
// service catalogue from /api/v1, and walks the user through a booking flow
// inside a Shadow DOM root for CSS isolation.

interface PublicService {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  imageUrl: string | null;
  requiresWaiver: boolean;
  priceIncludesGst: boolean;
  serviceKind: "ONE_OFF" | "TERM" | "HOLIDAY_PROGRAM";
  pricingModel: "FIXED" | "PER_HEAD" | "DURATION_TIER";
  defaultDurationMinutes: number;
  paymentMode: "FULL" | "DEPOSIT_PCT" | "DEPOSIT_FIXED" | "IN_PERSON";
  depositValue: number;
  /** Deposit services: customer may choose to pay the whole total now. Older servers omit it. */
  allowPayInFull?: boolean;
  /** Smallest accepted party size; null/absent = 1. Older servers omit it. */
  minPartySize?: number | null;
  variants: {
    id: string;
    label: string;
    priceCents: number;
    durationMinutes: number | null;
    /** Places left on a capped holiday day / term; null = unlimited. */
    placesLeft: number | null;
    /** Single-slot services: first bookable local date, null = none. Older servers omit it. */
    nextAvailable?: string | null;
    sessions: { id: string; startsAt: string; endsAt: string; label: string | null }[];
  }[];
  extras: {
    id: string;
    name: string;
    priceCents: number;
    unit: "PER_BOOKING" | "PER_HEAD";
    required: boolean;
    /** Only offered with this variant; null/absent = every variant. */
    variantId?: string | null;
  }[];
}

interface PublicCatalogue {
  tenant: {
    slug: string;
    name: string;
    theme: PublicWidgetTheme;
    waiver: PublicWaiverConfig;
    howHeardOptions: string[];
  };
  services: PublicService[];
}

interface PublicWidgetTheme {
  mode: "light" | "dark";
  accent: string | null;
  fontDisplay: string | null;
  fontBody: string | null;
}

interface PublicWaiverConfig {
  enabled: boolean;
  body: string;
  collectParticipants: boolean;
  maxParticipants: number;
  collectAddress: boolean;
  collectEmergencyContact: boolean;
  collectMedical: boolean;
}

interface WaiverParticipant {
  firstName: string;
  lastName: string;
  dob: string;
  age: string;
  gender: string;
}

interface WaiverData {
  signedName: string;
  agreed: boolean;
  completedByName: string;
  /** Drawn signature, captured as a PNG data URL. Empty until the customer
   * touches the canvas. Submitted alongside the typed name. */
  signaturePngBase64: string;
  participants: WaiverParticipant[];
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postcode: string;
  };
  emergencyContact: { name: string; relationship: string; phone: string };
  hasMedical: "" | "yes" | "no";
  medicalConditions: string;
}

interface SlotEntry {
  startsAt: string;
  endsAt: string;
  date: string;
  timeLocal: string;
}

type Step =
  | "list"
  | "variant"
  | "date"
  | "extras"
  | "customer"
  | "waiver"
  | "review"
  | "submitting"
  | "pay"
  | "paying"
  | "done"
  | "error";

// Minimal slice of the Stripe.js global we touch. Avoids pulling in @stripe/stripe-js
// types so the widget bundle stays tiny.
type StripeJsInstance = {
  elements: (opts: { clientSecret: string; appearance?: object }) => {
    create: (
      type: "payment",
      opts?: { layout?: "tabs" | "accordion" | "auto" },
    ) => { mount: (selector: Element | string) => void };
  };
  confirmPayment: (opts: {
    elements: ReturnType<StripeJsInstance["elements"]>;
    confirmParams?: { return_url?: string };
    redirect?: "always" | "if_required";
  }) => Promise<{
    error?: { message?: string; type?: string };
    paymentIntent?: { id: string; status: string };
  }>;
};
type StripeFactory = (publishableKey: string) => StripeJsInstance;

interface State {
  step: Step;
  tenant?: PublicCatalogue["tenant"];
  services: PublicService[];
  service?: PublicService;
  variant?: PublicService["variants"][number];
  dateYmd?: string;
  /** YYYY-MM of the month visible in the calendar grid. Defaults to current. */
  calendarMonth?: string;
  slot?: SlotEntry;
  availableSlots?: SlotEntry[];
  loadingAvailability?: boolean;
  /** Free slots per month, grouped by local date. key = variantId|YYYY-MM. */
  monthCache: Record<string, Record<string, SlotEntry[]>>;
  /** First bookable date per variant (null = none in range), for the option cards. */
  nextAvailable?: Record<string, string | null | undefined>;
  nextAvailableFor?: string;
  partySize: number;
  extras: Record<string, number>;
  customer: { name: string; email: string; phone: string };
  waiver: WaiverData;
  notes: string;
  howHeard: string;
  /** Deposit services with allowPayInFull: pay the whole total now. */
  payInFull: boolean;
  errorMessage?: string;
  confirmation?: {
    bookingId: string;
    totalCents: number;
    expiresAt: string;
  };
  payment?:
    | {
        provider: "STRIPE";
        amountCents: number;
        clientSecret: string;
        publishableKey: string;
      }
    | {
        provider: "SQUARE";
        amountCents: number;
        applicationId: string;
        locationId: string;
        environment: "SANDBOX" | "PRODUCTION";
        idempotencyKey: string;
      };
}

// Minimal slice of the Square Web SDK we touch.
interface SquareCard {
  attach(target: Element | string): Promise<void>;
  tokenize(): Promise<{ status: string; token?: string; errors?: Array<{ message: string }> }>;
  destroy(): Promise<void>;
}
interface SquarePayments {
  card(): Promise<SquareCard>;
}
type SquareGlobal = {
  payments: (applicationId: string, locationId: string) => SquarePayments;
};

// Widget styles. Tokens live as CSS variables on .root so a tenant's theme +
// accent override can be applied inline. Two named themes ship — "light" (the
// default Cal-style) and "dark" (Ninja-style navy + red).
const stylesCss = `
  .root, .root * { box-sizing: border-box; }
  .root {
    /* Light theme defaults */
    --w-bg: #ffffff;
    --w-bg-subtle: #f9fafb;
    --w-bg-muted: #f3f4f6;
    --w-bg-emphasis: #e5e7eb;
    --w-text: #111827;
    --w-text-emphasis: #030712;
    --w-text-subtle: #4b5563;
    --w-text-muted: #6b7280;
    --w-border: #e5e7eb;
    --w-border-subtle: #f3f4f6;
    --w-brand: #111111;
    --w-brand-text: #ffffff;
    --w-brand-shadow: rgba(0,0,0,.18);
    --w-error: #b91c1c;
    --w-error-bg: #fef2f2;
    --w-radius-btn: 10px;
    --w-radius-card: 16px;
    --w-radius-input: 6px;
    --w-radius-chip: 999px;
    --w-font-body: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    --w-font-display: var(--w-font-body);
    --w-letter-display: -0.02em;

    font-family: var(--w-font-body);
    color: var(--w-text);
    line-height: 1.5;
  }

  /* Dark theme — Ninja-style navy + red. Tenant accent overrides --w-brand
     inline; if no accent is set, dark mode defaults to a punchy red. */
  .root[data-theme="dark"] {
    --w-bg: #0A1628;
    --w-bg-subtle: #111D33;
    --w-bg-muted: #1A2840;
    --w-bg-emphasis: #1A2840;
    --w-text: #FFFFFF;
    --w-text-emphasis: #FFFFFF;
    --w-text-subtle: #A1A1AA;
    --w-text-muted: #52525B;
    --w-border: rgba(255,255,255,0.08);
    --w-border-subtle: rgba(255,255,255,0.04);
    --w-brand: #CC0000;
    --w-brand-text: #FFFFFF;
    --w-brand-shadow: rgba(204,0,0,.30);
    --w-error: #fecaca;
    --w-error-bg: #2a0a0a;
    --w-radius-btn: 4px;
    --w-radius-card: 8px;
    --w-radius-input: 4px;
    --w-radius-chip: 4px;
    --w-letter-display: 0.02em;
  }

  .card {
    background: var(--w-bg);
    border: 1px solid var(--w-border);
    border-radius: var(--w-radius-card);
    padding: 28px;
    max-width: 640px;
  }
  .card h2 {
    margin: 0 0 6px;
    font-family: var(--w-font-display);
    font-size: 26px;
    font-weight: 600;
    color: var(--w-text-emphasis);
    letter-spacing: var(--w-letter-display);
    line-height: 1.05;
  }
  .root[data-theme="dark"] .card h2 { font-size: 38px; font-weight: 400; }
  .card h3 {
    margin: 0 0 8px;
    font-family: var(--w-font-display);
    font-size: 17px;
    font-weight: 600;
    color: var(--w-text-emphasis);
    letter-spacing: var(--w-letter-display);
  }
  .root[data-theme="dark"] .card h3 { font-size: 22px; font-weight: 400; }
  .muted { color: var(--w-text-muted); font-size: 14px; margin: 0 0 16px; }
  .cat { font-size: 12px; color: var(--w-text-muted); }
  .services { display: grid; gap: 12px; }
  .service {
    border: 1px solid var(--w-border); border-radius: var(--w-radius-card); padding: 18px 20px;
    background: var(--w-bg-subtle);
    transition: border-color .2s ease, background .2s ease, transform .2s ease;
  }
  .service:hover { border-color: var(--w-brand); background: var(--w-bg-muted); }
  .root[data-theme="dark"] .service:hover { transform: translateY(-2px); }
  .service-head { display: flex; justify-content: space-between; align-items: start; gap: 12px; margin-bottom: 4px; }
  .price { font-variant-numeric: tabular-nums; font-size: 14px; color: var(--w-text-emphasis); font-weight: 600; }
  .desc { margin: 8px 0 14px; font-size: 14px; color: var(--w-text-subtle); line-height: 1.5; }
  .btn {
    appearance: none; cursor: pointer;
    background: var(--w-brand); color: var(--w-brand-text);
    border: 0; border-radius: var(--w-radius-btn);
    padding: 10px 18px; font-size: 14px; font-weight: 700;
    transition: opacity .2s ease, transform .2s ease, box-shadow .2s ease;
  }
  .btn:hover { opacity: .92; }
  .root[data-theme="dark"] .btn { letter-spacing: 0.02em; text-transform: uppercase; font-size: 13px; padding: 12px 22px; }
  .root[data-theme="dark"] .btn:hover { opacity: 1; transform: translateY(-2px); box-shadow: 0 8px 24px var(--w-brand-shadow); }
  .btn[disabled] { opacity: .5; cursor: not-allowed; transform: none; box-shadow: none; }
  .btn-ghost {
    appearance: none; cursor: pointer;
    background: transparent; color: var(--w-text-emphasis);
    border: 1px solid var(--w-border); border-radius: var(--w-radius-btn);
    padding: 10px 18px; font-size: 14px; font-weight: 500;
    transition: background .2s ease, border-color .2s ease;
  }
  .btn-ghost:hover { background: var(--w-bg-muted); border-color: var(--w-text-muted); }
  .root[data-theme="dark"] .btn-ghost { letter-spacing: 0.02em; text-transform: uppercase; font-size: 13px; font-weight: 700; padding: 12px 22px; }
  .back {
    appearance: none; cursor: pointer; background: none; border: 0;
    color: var(--w-text-muted); padding: 0; margin: 0 0 12px;
    font-size: 13px;
    transition: color .12s ease;
  }
  .back:hover { color: var(--w-text-emphasis); }
  .row { display: flex; gap: 12px; align-items: end; flex-wrap: wrap; }
  .col { display: flex; flex-direction: column; gap: 4px; }
  label { font-size: 12px; color: var(--w-text-emphasis); font-weight: 500; }
  input[type="text"], input[type="email"], input[type="tel"], input[type="number"], input[type="date"], input[type="time"], textarea, select {
    width: 100%; padding: 10px 12px; font-size: 14px;
    border: 1px solid var(--w-border); border-radius: var(--w-radius-input);
    background: var(--w-bg-subtle); color: var(--w-text);
    transition: border-color .15s ease, box-shadow .15s ease;
    font-family: inherit;
  }
  input:focus, textarea:focus, select:focus {
    outline: none; border-color: var(--w-brand);
    box-shadow: 0 0 0 2px var(--w-brand-shadow);
  }
  .variants, .dates, .times { display: flex; flex-wrap: wrap; gap: 8px; }
  .chip {
    appearance: none; cursor: pointer;
    border: 1px solid var(--w-border); border-radius: var(--w-radius-chip);
    padding: 8px 14px; font-size: 13px; background: var(--w-bg-subtle);
    color: var(--w-text);
    transition: border-color .15s ease, background .15s ease, color .15s ease;
  }
  .chip:hover { border-color: var(--w-brand); }
  .chip[disabled] { cursor: not-allowed; opacity: .45; }
  .chip[aria-pressed="true"] {
    background: var(--w-brand); color: var(--w-brand-text); border-color: var(--w-brand);
  }
  .extra { display: flex; align-items: center; justify-content: space-between; gap: 12px;
           border: 1px solid var(--w-border); border-radius: var(--w-radius-card); padding: 12px 14px;
           background: var(--w-bg-subtle); }
  .extra-name { flex: 1; font-size: 14px; }
  .extra-meta { color: var(--w-text-muted); font-size: 12px; }
  .qty { width: 70px; }
  .lines { width: 100%; border-collapse: collapse; font-size: 14px; }
  .lines td { padding: 6px 0; border-bottom: 1px dashed var(--w-border); }
  .lines tr:last-child td { border: 0; }
  .lines .total td { font-weight: 600; padding-top: 12px; color: var(--w-text-emphasis); }
  .lines .gst td { color: var(--w-text-muted); font-size: 12px; }
  .err { border-color: var(--w-error); background: var(--w-error-bg); color: var(--w-error); }
  .empty {
    padding: 16px; color: var(--w-text-muted); font-size: 14px; text-align: center;
    border: 1px dashed var(--w-border); border-radius: 16px;
  }
  .loading { padding: 16px; color: var(--w-text-muted); font-size: 14px; }
  .footer { display: flex; gap: 8px; margin-top: 16px; }

  /* Signature pad ----------------------------------------------------------- */
  .sig-wrap {
    position: relative;
    border: 1px solid var(--w-border);
    border-radius: var(--w-radius-input);
    background: var(--w-bg-subtle);
    margin-top: 6px;
    overflow: hidden;
  }
  .sig-canvas {
    display: block;
    width: 100%;
    height: 140px;
    cursor: crosshair;
    touch-action: none;
  }
  .sig-hint {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
    color: var(--w-text-muted); font-size: 12px;
    transition: opacity .15s ease;
  }
  .sig-hint--hidden { opacity: 0; }

  /* Calendar (date picker) -------------------------------------------------- */
  .cal-wrap { margin-top: 14px; }
  .cal {
    border: 1px solid var(--w-border);
    border-radius: var(--w-radius-card);
    padding: 16px;
    background: var(--w-bg);
  }
  .cal__head {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 10px;
  }
  .cal__nav {
    appearance: none; background: transparent; cursor: pointer;
    border: 1px solid var(--w-border); color: var(--w-text-subtle);
    width: 32px; height: 32px; border-radius: var(--w-radius-input);
    display: inline-flex; align-items: center; justify-content: center;
    transition: border-color .15s, background .15s, color .15s;
  }
  .cal__nav:hover:not([disabled]) {
    border-color: var(--w-text-muted); color: var(--w-text-emphasis);
    background: var(--w-bg-muted);
  }
  .cal__nav[disabled] { opacity: .3; cursor: not-allowed; }
  .cal__month {
    font-family: var(--w-font-display);
    font-size: 16px; font-weight: 600;
    letter-spacing: var(--w-letter-display);
    color: var(--w-text-emphasis);
  }
  .root[data-theme="dark"] .cal__month { font-size: 22px; font-weight: 400; }
  .cal__weekdays {
    display: grid; grid-template-columns: repeat(7, 1fr);
    margin-bottom: 4px;
  }
  .cal__weekday {
    text-align: center; padding: 8px 0;
    font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: .12em;
    color: var(--w-text-muted);
  }
  .cal__grid {
    display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px;
  }
  .cal__day {
    appearance: none; background: transparent; border: 0; cursor: pointer;
    height: 38px;
    border-radius: var(--w-radius-input);
    font-family: inherit; font-size: 13px; color: var(--w-text);
    display: inline-flex; align-items: center; justify-content: center;
    transition: background .12s, color .12s, border-color .12s;
  }
  .cal__day:hover:not([disabled]):not(.cal__day--selected) {
    background: var(--w-bg-muted);
  }
  .cal__day--outside { color: var(--w-text-muted); opacity: .35; }
  .cal__day--today:not(.cal__day--selected) {
    border: 1px solid var(--w-border);
  }
  .cal__day--selected {
    background: var(--w-brand); color: var(--w-brand-text);
    font-weight: 600;
  }
  .cal__day[disabled] {
    color: var(--w-text-muted); opacity: .35; cursor: not-allowed;
  }
  .cal__day--available:not(.cal__day--selected) {
    border: 1px solid var(--w-brand); color: var(--w-text-emphasis); font-weight: 700;
  }
  .cal__note { margin-top: 10px; font-size: 13px; color: var(--w-text-muted); text-align: center; }

  /* Option cards (e.g. Standard vs Extended party) --------------------------- */
  .vcards { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
  .vcard {
    appearance: none; text-align: left; cursor: pointer; font-family: inherit;
    border: 1px solid var(--w-border); border-radius: var(--w-radius-card);
    background: var(--w-bg-subtle); color: var(--w-text);
    padding: 20px; display: flex; flex-direction: column; gap: 6px;
    transition: border-color .15s ease, transform .15s ease;
  }
  .vcard:hover:not([disabled]) { border-color: var(--w-brand); transform: translateY(-1px); }
  .vcard[disabled] { opacity: .5; cursor: not-allowed; }
  .vcard__label {
    font-family: var(--w-font-display); font-size: 22px; line-height: 1.1;
    letter-spacing: var(--w-letter-display); color: var(--w-text-emphasis);
  }
  .vcard__dur { font-size: 13px; color: var(--w-text-muted); }
  .vcard__price { font-size: 20px; font-weight: 700; color: var(--w-text-emphasis); font-variant-numeric: tabular-nums; }
  .vcard__price small { font-size: 12px; font-weight: 500; color: var(--w-text-muted); }
  .vcard__next { font-size: 13px; color: var(--w-text-subtle); }
  .vcard__cta { margin-top: 8px; font-size: 13px; font-weight: 700; color: var(--w-brand); }

  /* OS dark-mode does NOT auto-apply — tenants choose their theme explicitly
     via Admin → Settings → Booking widget. This keeps the embed predictable
     when dropped onto a customer's site that may have its own scheme. */
`;

const audFmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });
const fmtCents = (c: number) => audFmt.format(c / 100);

/** 90 → "1 hr 30 min", 120 → "2 hours", 45 → "45 min". */
function fmtDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} ${h === 1 ? "hour" : "hours"}`;
  return `${h} hr ${m} min`;
}

/** "2026-10-03" → "Sat 3 Oct". */
function fmtYmd(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

function escape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

(function () {
  const currentScript = document.currentScript as HTMLScriptElement | null;
  if (!currentScript) {
    console.error("[CTMS] widget could not locate its <script> tag");
    return;
  }
  const apiBase = new URL(currentScript.src).origin;
  const modal =
    currentScript.dataset.modal === "1" || currentScript.dataset.modal === "true";
  // Optional: skip the service list and open straight onto one service.
  const preselectServiceId = currentScript.dataset.service ?? null;
  // Optional: comma-separated service ids this embed never offers (e.g. a
  // site that shares the tenant but doesn't sell one of its services).
  const excludedServiceIds = new Set(
    (currentScript.dataset.exclude ?? "").split(",").map((x) => x.trim()).filter(Boolean),
  );
  // Default headcount. 10 suits a birthday party; a trial class embed should
  // pass data-party-size="1" so one booking doesn't reserve the whole class.
  const defaultPartySize = Number(currentScript.dataset.partySize ?? "10");
  const partySizeExplicit = currentScript.dataset.partySize != null;
  const initialPartySize =
    Number.isFinite(defaultPartySize) && defaultPartySize > 0 ? Math.floor(defaultPartySize) : 10;

  // Inline (the default) renders into the host page's mount element. Modal
  // mode builds its own overlay and opens from a trigger, so a page that only
  // wants a "Book now" button doesn't need a layout slot for the widget.
  let hostEl: Element;
  let openModal = () => {};
  let closeModal = () => {};

  if (modal) {
    const overlay = document.createElement("div");
    overlay.setAttribute("data-ctms-overlay", "");
    overlay.style.cssText = [
      "position:fixed",
      "inset:0",
      "background:rgba(0,0,0,0.55)",
      "display:none",
      "z-index:2147483000",
      "overflow-y:auto",
      "padding:24px 16px",
      "-webkit-overflow-scrolling:touch",
    ].join(";");

    const panel = document.createElement("div");
    panel.style.cssText = "max-width:640px;margin:0 auto;position:relative";

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.setAttribute("aria-label", "Close booking");
    closeBtn.textContent = "\u00d7";
    closeBtn.style.cssText = [
      "position:absolute",
      "top:-4px",
      "right:-4px",
      "z-index:1",
      "width:34px",
      "height:34px",
      "border-radius:50%",
      "border:none",
      "cursor:pointer",
      "font-size:22px",
      "line-height:1",
      "background:#fff",
      "color:#111",
      "box-shadow:0 2px 8px rgba(0,0,0,0.3)",
    ].join(";");

    panel.appendChild(closeBtn);
    overlay.appendChild(panel);
    // Safe to load from <head>: attach once <body> exists.
    if (document.body) document.body.appendChild(overlay);
    else document.addEventListener("DOMContentLoaded", () => document.body.appendChild(overlay));
    hostEl = panel;

    let lastFocused: Element | null = null;
    openModal = () => {
      lastFocused = document.activeElement;
      overlay.style.display = "block";
      document.body.style.overflow = "hidden";
      closeBtn.focus();
    };
    closeModal = () => {
      overlay.style.display = "none";
      document.body.style.overflow = "";
      if (lastFocused instanceof HTMLElement) lastFocused.focus();
    };

    closeBtn.addEventListener("click", closeModal);
    // Backdrop click closes; clicks inside the panel must not.
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) closeModal();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && overlay.style.display === "block") closeModal();
    });

    const triggerSelector = currentScript.dataset.trigger;
    if (triggerSelector) {
      // Delegated, so this works whether the script runs before or after
      // the page's buttons exist (it can be loaded early from <head>).
      document.addEventListener("click", (e) => {
        const el = (e.target as Element | null)?.closest?.(triggerSelector);
        if (!el) return;
        e.preventDefault();
        openModal();
      });
      // Tell host pages' early-click shims which triggers are now live.
      const reg = window as unknown as { __ctmsTriggers?: string[] };
      (reg.__ctmsTriggers ??= []).push(triggerSelector);
      document.querySelectorAll<HTMLElement>(triggerSelector).forEach((el) => {
        el.dataset.ctmsBound = "1";
      });
      // A host page may catch clicks made before this script loaded and park
      // the element in window.__ctmsPending; open for it if it's ours.
      const w = window as unknown as { __ctmsPending?: Element | null };
      if (w.__ctmsPending?.matches(triggerSelector)) {
        w.__ctmsPending = null;
        openModal();
      }
    }
    // Let host pages open it from their own code too.
    (window as unknown as Record<string, unknown>).ctmsOpenBooking = openModal;
  } else {
    const mountSelector = currentScript.dataset.mount ?? "#ctms-booking";
    const found = document.querySelector(mountSelector);
    if (!found) {
      console.error(`[CTMS] mount element ${mountSelector} not found`);
      return;
    }
    hostEl = found;
  }

  const shadow = hostEl.attachShadow({ mode: "open" });
  const styleEl = document.createElement("style");
  styleEl.textContent = stylesCss;
  shadow.appendChild(styleEl);
  // Light-DOM children of the host must be slotted to render at all: the
  // modal's close button (default slot) and the Square card field
  // ("ctms-payment" slot — Square's SDK can't attach inside a shadow root).
  shadow.appendChild(document.createElement("slot"));
  const root = document.createElement("div");
  root.className = "root";
  shadow.appendChild(root);

  const state: State = {
    step: "list",
    services: [],
    monthCache: {},
    partySize: initialPartySize,
    extras: {},
    customer: { name: "", email: "", phone: "" },
    waiver: emptyWaiver(),
    notes: "",
    howHeard: "",
    payInFull: false,
  };

  function emptyWaiver(): WaiverData {
    return {
      signedName: "",
      agreed: false,
      completedByName: "",
      signaturePngBase64: "",
      participants: [emptyParticipant()],
      address: { line1: "", line2: "", city: "", state: "", postcode: "" },
      emergencyContact: { name: "", relationship: "", phone: "" },
      hasMedical: "",
      medicalConditions: "",
    };
  }

  function emptyParticipant(): WaiverParticipant {
    return { firstName: "", lastName: "", dob: "", age: "", gender: "" };
  }

  function setState(patch: Partial<State>) {
    Object.assign(state, patch);
    render();
  }

  function api<T>(path: string, init?: RequestInit): Promise<T> {
    // Only send content-type with a body: on a GET it makes the request
    // "non-simple", costing a CORS preflight round trip per URL.
    // "same-origin" sends no cookies cross-origin (same as "omit"), and lets a
    // host page's <link rel="preload" as="fetch" crossorigin> satisfy this
    // request — pages can start the catalogue download before we even run.
    return fetch(`${apiBase}${path}`, {
      credentials: "same-origin",
      headers: { ...(init?.body ? { "content-type": "application/json" } : {}), ...(init?.headers ?? {}) },
      ...init,
    }).then(async (r) => {
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.message ?? body?.error ?? `HTTP ${r.status}`);
      }
      return r.json();
    });
  }

  function render() {
    root.innerHTML = `<div class="card">${views[state.step]()}</div>`;
    attach();
  }

  // --- Views ----------------------------------------------------------------

  const views: Record<Step, () => string> = {
    list: () => `
      <h2>Book with ${escape(state.tenant?.name ?? "")}</h2>
      <p class="muted">Choose a service.</p>
      ${!state.tenant
        ? `<div class="empty">Loading…</div>`
        : state.services.length === 0
        ? `<div class="empty">No services available.</div>`
        : `<div class="services">${state.services
            .map(serviceCard)
            .join("")}</div>`}
    `,

    variant: () => {
      const s = state.service!;
      if (!isMultiSession()) return variantCards(s);
      return `
        ${backBtn("list")}
        <h2>${escape(s.name)}</h2>
        ${s.description ? `<p class="muted">${escape(s.description)}</p>` : `<p class="muted">Choose an option.</p>`}
        <div class="variants">
          ${s.variants
            .map(
              (v) => `
              <button type="button" class="chip" aria-pressed="${state.variant?.id === v.id}"
                      data-variant="${v.id}" ${v.placesLeft === 0 ? "disabled" : ""}>
                ${escape(v.label)} · ${v.placesLeft === 0 ? "Full" : `${fmtCents(v.priceCents)}${s.priceIncludesGst ? "" : " +GST"}`}
                ${v.placesLeft != null && v.placesLeft > 0 && v.placesLeft <= 5 ? `<span class="cat"> · ${v.placesLeft} left</span>` : ""}
                ${v.durationMinutes ? `<span class="cat"> · ${v.durationMinutes} min</span>` : ""}
                ${v.sessions.length ? `<span class="cat"> · ${v.sessions.length} sessions</span>` : ""}
              </button>`,
            )
            .join("")}
        </div>
        ${
          isMultiSession() && state.variant
            ? `<div style="margin-top:12px;padding:12px;border:1px solid var(--w-border);border-radius:12px;background:var(--w-bg-subtle)">
                <div class="cat" style="font-weight:600;margin-bottom:6px">Included sessions</div>
                ${sessionList(state.variant.sessions)}
              </div>`
            : ""
        }
        <div class="footer">
          <button class="btn" id="next" ${state.variant ? "" : "disabled"}>Continue</button>
        </div>
      `;
    },

    date: () => `
      ${backBtn("variant")}
      <h2>Pick a date &amp; time</h2>
      <div class="cal-wrap">${calendarGrid()}</div>
      <h3 style="margin-top:20px">Time</h3>
      <div class="times" id="times">${
        state.loadingAvailability
          ? `<div class="loading">Loading times…</div>`
          : timesChips()
      }</div>
      <div class="footer">
        <button class="btn" id="next" ${state.slot ? "" : "disabled"}>Continue</button>
      </div>
    `,

    extras: () => {
      const s = state.service!;
      return `
        ${backBtn("date")}
        <h2>Details &amp; extras</h2>
        <div class="row" style="margin-bottom:12px">
          <div class="col" style="width:120px">
            <label>${s.pricingModel === "PER_HEAD" ? "Number of people" : "Party size"}</label>
            <input type="number" min="${minPartySize()}" max="${state.variant?.placesLeft ?? 200}" class="qty" id="partySize" value="${state.partySize}" />
          </div>
        </div>
        ${minPartySize() > 1 ? `<p class="muted">Minimum ${minPartySize()}.</p>` : ""}
        ${
          extrasForVariant().length === 0
            ? `<p class="muted">No extras for this service.</p>`
            : `<div style="display:grid;gap:8px">${extrasForVariant()
                .map(extraRow)
                .join("")}</div>`
        }
        <div class="footer">
          <button class="btn" id="next">Continue</button>
        </div>
      `;
    },

    customer: () => `
      ${backBtn("extras")}
      <h2>Your details</h2>
      <div style="display:grid;gap:12px">
        <div class="col">
          <label>Your name</label>
          <input type="text" id="cust-name" value="${escape(state.customer.name)}" required />
        </div>
        <div class="col">
          <label>Email</label>
          <input type="email" id="cust-email" value="${escape(state.customer.email)}" required />
        </div>
        <div class="col">
          <label>Phone</label>
          <input type="tel" id="cust-phone" value="${escape(state.customer.phone)}" />
        </div>
        ${
          (state.tenant?.howHeardOptions ?? []).length
            ? `<div class="col">
          <label>How did you hear about us?</label>
          <select id="how-heard">
            <option value="">Select an option</option>
            ${(state.tenant?.howHeardOptions ?? [])
              .map(
                (o) =>
                  `<option value="${escape(o)}"${state.howHeard === o ? " selected" : ""}>${escape(o)}</option>`,
              )
              .join("")}
          </select>
        </div>`
            : ""
        }
        <div class="col">
          <label>Anything else we should know? (optional)</label>
          <textarea id="notes" rows="3" placeholder="Allergies, injuries, a nervous first-timer, running late — anything that helps us look after you.">${escape(state.notes)}</textarea>
        </div>
      </div>
      <div class="footer">
        <button class="btn" id="next">Continue</button>
      </div>
    `,

    waiver: () => renderWaiverStep(),

    review: () => {
      const breakdown = previewPrice();
      const whenLine = isMultiSession()
        ? `${state.variant?.sessions.length ?? 0} session${state.variant?.sessions.length === 1 ? "" : "s"}`
        : `${state.slot?.date ?? ""} at ${state.slot?.timeLocal ?? ""}`;
      return `
        ${backBtn(state.service?.requiresWaiver ? "waiver" : "customer")}
        <h2>Review &amp; book</h2>
        <p class="muted">${escape(state.service?.name ?? "")} · ${escape(state.variant?.label ?? "")} · ${escape(whenLine)}</p>
        <table class="lines">
          ${breakdown.lines
            .map(
              (l) => `<tr><td>${escape(l.label)}</td><td style="text-align:right">${fmtCents(l.lineTotalCents)}</td></tr>`,
            )
            .join("")}
          <tr class="gst"><td>${state.service?.priceIncludesGst ? "Includes GST" : "GST (10%)"}</td><td style="text-align:right">${fmtCents(breakdown.gstCents)}</td></tr>
          <tr class="total"><td>Total</td><td style="text-align:right">${fmtCents(breakdown.totalCents)}</td></tr>
        </table>
        ${
          canPayInFull() && depositCents(breakdown.totalCents) < breakdown.totalCents
            ? `<div class="col" style="margin-top:12px">
          <label>How would you like to pay?</label>
          <div style="display:flex;flex-direction:column;gap:6px;font-size:13px;padding-top:4px">
            <label style="display:flex;gap:6px;align-items:center;font-weight:400">
              <input type="radio" name="payOption" value="deposit" ${state.payInFull ? "" : "checked"} /> Pay ${fmtCents(depositCents(breakdown.totalCents))} deposit now, the rest on the day
            </label>
            <label style="display:flex;gap:6px;align-items:center;font-weight:400">
              <input type="radio" name="payOption" value="full" ${state.payInFull ? "checked" : ""} /> Pay in full now (${fmtCents(breakdown.totalCents)})
            </label>
          </div>
        </div>`
            : dueNowCents(breakdown.totalCents) < breakdown.totalCents
            ? `<p class="muted" style="margin-top:12px">Pay a ${fmtCents(dueNowCents(breakdown.totalCents))} deposit now — the remaining ${fmtCents(breakdown.totalCents - dueNowCents(breakdown.totalCents))} is payable on the day.</p>`
            : ""
        }
        ${state.errorMessage ? `<p class="muted err" style="margin-top:12px;padding:8px 12px;border-radius:8px;border:1px solid;">${escape(state.errorMessage)}</p>` : ""}
        <div class="footer">
          <button class="btn" id="submit">Book — pay ${fmtCents(dueNowCents(breakdown.totalCents))}${dueNowCents(breakdown.totalCents) < breakdown.totalCents ? " deposit" : ""}</button>
        </div>
      `;
    },

    submitting: () => `<div class="loading">Reserving your slot…</div>`,

    pay: () => {
      const total = state.confirmation?.totalCents ?? 0;
      const dueNow = state.payment?.amountCents ?? total;
      const isDeposit = dueNow < total;
      return `
      <h2>Payment</h2>
      <p class="muted">${
        isDeposit
          ? `Pay a ${fmtCents(dueNow)} deposit now — the remaining ${fmtCents(total - dueNow)} is payable on the day.`
          : `Total ${fmtCents(total)}.`
      }</p>
      <div id="payment-mount" style="min-height:96px"><slot name="ctms-payment"></slot></div>
      ${state.errorMessage ? `<p class="muted err" style="margin-top:12px;padding:8px 12px;border-radius:8px;border:1px solid">${escape(state.errorMessage)}</p>` : ""}
      <div class="footer">
        <button class="btn" id="pay">Pay ${fmtCents(dueNow)}${isDeposit ? " deposit" : ""}</button>
      </div>
    `;
    },

    paying: () => `<div class="loading">Processing payment…</div>`,

    done: () => {
      const c = state.confirmation!;
      const wasPaid = !state.errorMessage;
      return `
        <h2>${wasPaid ? "Booking confirmed ✓" : "Booking pending"}</h2>
        <p class="muted">${
          wasPaid
            ? "We've emailed your receipt and waiver. See you on the day!"
            : `Your reservation is held until ${new Date(c.expiresAt).toLocaleTimeString()}.`
        }</p>
        <table class="lines">
          <tr><td>Reference</td><td style="text-align:right;font-family:monospace">${escape(c.bookingId)}</td></tr>
          <tr class="total"><td>Total</td><td style="text-align:right">${fmtCents(c.totalCents)}</td></tr>
          ${
            wasPaid && state.payment && state.payment.amountCents < c.totalCents
              ? `<tr><td>Paid today</td><td style="text-align:right">${fmtCents(state.payment.amountCents)}</td></tr>
          <tr><td>Balance due on the day</td><td style="text-align:right">${fmtCents(c.totalCents - state.payment.amountCents)}</td></tr>`
              : ""
          }
        </table>
        ${state.errorMessage ? `<p class="muted err" style="margin-top:16px;padding:8px 12px;border-radius:8px;border:1px solid">${escape(state.errorMessage)}</p>` : ""}
      `;
    },

    error: () => `
      <h2>Something went wrong</h2>
      <p class="muted err" style="padding:8px 12px;border-radius:8px;border:1px solid">${escape(state.errorMessage ?? "Unknown error")}</p>
      <div class="footer">
        <button class="btn-ghost" id="restart">Start over</button>
      </div>
    `,
  };

  function minPartySize(): number {
    return Math.max(1, state.service?.minPartySize ?? 1);
  }

  /** Extras offered with the chosen variant (variant-specific ones only with theirs). */
  function extrasForVariant(): PublicService["extras"] {
    const s = state.service;
    if (!s) return [];
    return s.extras.filter((e) => !e.variantId || e.variantId === state.variant?.id);
  }

  function canPayInFull(): boolean {
    const svc = state.service;
    return !!svc?.allowPayInFull && (svc.paymentMode === "DEPOSIT_FIXED" || svc.paymentMode === "DEPOSIT_PCT");
  }

  function dueNowCents(totalCents: number): number {
    if (state.payInFull && canPayInFull()) return totalCents;
    return depositCents(totalCents);
  }

  function depositCents(totalCents: number): number {
    const svc = state.service;
    if (!svc) return totalCents;
    if (svc.paymentMode === "DEPOSIT_FIXED" && svc.depositValue > 0) {
      return Math.min(svc.depositValue, totalCents);
    }
    if (svc.paymentMode === "DEPOSIT_PCT" && svc.depositValue > 0 && svc.depositValue < 100) {
      return Math.min(totalCents, Math.round((totalCents * svc.depositValue) / 100));
    }
    return totalCents;
  }

  function backBtn(toStep: Step): string {
    return `<button type="button" class="back" data-go="${toStep}">← Back</button>`;
  }

  function serviceCard(s: PublicService): string {
    const prices = s.variants.length ? s.variants.map((v) => v.priceCents) : [0];
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return `
      <div class="service">
        <div class="service-head">
          <div>
            <h3>${escape(s.name)}</h3>
            ${s.category ? `<span class="cat">${escape(s.category)}</span>` : ""}
          </div>
          <div class="price">${min === max ? fmtCents(min) : `${fmtCents(min)} – ${fmtCents(max)}`}${s.priceIncludesGst ? "" : " +GST"}</div>
        </div>
        ${s.description ? `<p class="desc">${escape(s.description)}</p>` : ""}
        <button type="button" class="btn" data-service="${s.id}">Book ${escape(s.name)}</button>
      </div>
    `;
  }

  /** YYYY-MM-DD in local time (avoids the UTC drift you get from toISOString()). */
  function ymdLocal(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function calendarGrid(): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayYmd = ymdLocal(today);

    // Anchor: the month currently shown. Defaults to today's month.
    const anchorKey = state.calendarMonth ?? todayYmd.slice(0, 7);
    const [yy, mm] = anchorKey.split("-").map(Number);
    const first = new Date(yy, mm - 1, 1);
    const startDow = first.getDay(); // 0 = Sunday

    const monthLabel = first.toLocaleDateString("en-AU", {
      month: "long",
      year: "numeric",
    });

    const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Only dates with at least one free slot are selectable.
    const monthKey = `${state.variant?.id}|${anchorKey}`;
    const monthLoaded = monthKey in state.monthCache;
    const byDate = state.monthCache[monthKey] ?? {};
    const note = !monthLoaded
      ? "Checking availability…"
      : Object.keys(byDate).length === 0
      ? "No dates available this month — try the next month."
      : "";

    const cells: string[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(yy, mm - 1, 1 - startDow + i);
      const dymd = ymdLocal(d);
      const outside = d.getMonth() !== mm - 1;
      const isToday = dymd === todayYmd;
      const isPast = dymd < todayYmd;
      const selected = state.dateYmd === dymd;
      const available = Boolean(monthLoaded && byDate[dymd]?.length);
      const disabled = isPast || outside || !available;
      const cls = [
        "cal__day",
        available && !outside ? "cal__day--available" : "",
        outside ? "cal__day--outside" : "",
        isToday ? "cal__day--today" : "",
        selected ? "cal__day--selected" : "",
      ]
        .filter(Boolean)
        .join(" ");
      cells.push(
        `<button type="button" class="${cls}" data-date="${dymd}" aria-pressed="${selected}" ${disabled ? "disabled" : ""}>${d.getDate()}</button>`,
      );
    }

    // Disable "previous" when we're already showing the current month — we
    // never let the customer browse into the past.
    const showingCurrentMonth = anchorKey === todayYmd.slice(0, 7);

    return `
      <div class="cal">
        <div class="cal__head">
          <button type="button" class="cal__nav" data-nav="prev" aria-label="Previous month" ${showingCurrentMonth ? "disabled" : ""}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </button>
          <span class="cal__month">${escape(monthLabel)}</span>
          <button type="button" class="cal__nav" data-nav="next" aria-label="Next month">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
        </div>
        <div class="cal__weekdays">
          ${weekdays.map((w) => `<div class="cal__weekday">${w}</div>`).join("")}
        </div>
        <div class="cal__grid">${cells.join("")}</div>
        ${note ? `<div class="cal__note">${note}</div>` : ""}
      </div>
    `;
  }

  function variantCards(s: PublicService): string {
    const na = state.nextAvailable;
    const gst = s.priceIncludesGst ? "" : " <small>+GST</small>";
    return `
      ${backBtn("list")}
      <h2>${escape(s.name)}</h2>
      ${s.description ? `<p class="muted">${escape(s.description)}</p>` : `<p class="muted">Choose an option.</p>`}
      <div class="vcards">
        ${s.variants
          .map((v) => {
            const next = na?.[v.id];
            const nextText =
              na === undefined ? "Checking availability…"
              : next ? `Next available: ${escape(fmtYmd(next))}`
              : next === null ? "No dates available right now"
              : "";
            return `
              <button type="button" class="vcard" data-variant-card="${v.id}" ${next === null ? "disabled" : ""}>
                <span class="vcard__label">${escape(v.label)}</span>
                ${v.durationMinutes ? `<span class="vcard__dur">${fmtDuration(v.durationMinutes)}</span>` : ""}
                <span class="vcard__price">${fmtCents(v.priceCents)}${gst}</span>
                <span class="vcard__next">${nextText}</span>
                <span class="vcard__cta">Choose ${escape(v.label)} →</span>
              </button>`;
          })
          .join("")}
      </div>
    `;
  }

  /** First bookable date for each variant, shown on the option cards. The
   * catalogue carries it; older servers don't, so fall back to fetching. */
  async function loadNextAvailable(service: PublicService) {
    if (state.nextAvailableFor === service.id) return;
    state.nextAvailableFor = service.id; // guard only — no render
    if (service.variants.every((v) => v.nextAvailable !== undefined)) {
      const known = Object.fromEntries(service.variants.map((v) => [v.id, v.nextAvailable]));
      setState({ nextAvailable: known });
      prefetchMonths(service, known);
      return;
    }
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + 120);
    const results = await Promise.all(
      service.variants.map((v) =>
        api<{ slots: SlotEntry[] }>(
          `/api/v1/services/${service.id}/availability?variantId=${v.id}&from=${ymdLocal(from)}&to=${ymdLocal(to)}`,
        )
          .then((d) => [v.id, d.slots[0]?.date ?? null] as const)
          .catch(() => [v.id, undefined] as const),
      ),
    );
    if (state.service?.id !== service.id) return;
    const found = Object.fromEntries(results);
    setState({ nextAvailable: found });
    prefetchMonths(service, found);
  }

  /** While the option cards are up, fetch each option's first month so the
   * calendar opens instantly. */
  function prefetchMonths(service: PublicService, next: Record<string, string | null | undefined>) {
    for (const v of service.variants) {
      const ymd = next[v.id];
      if (ymd) fetchMonth(service.id, v.id, ymd.slice(0, 7));
    }
  }

  const monthInFlight: Record<string, Promise<void>> = {};

  /** Fetch every free slot in a month once, grouped by date, into the cache. */
  function fetchMonth(serviceId: string, variantId: string, month: string): Promise<void> {
    const key = `${variantId}|${month}`;
    if (key in state.monthCache) return Promise.resolve();
    const [y, m] = month.split("-").map(Number);
    const lastDay = String(new Date(y, m, 0).getDate()).padStart(2, "0");
    return (monthInFlight[key] ??= api<{ slots: SlotEntry[] }>(
      `/api/v1/services/${serviceId}/availability?variantId=${variantId}&from=${month}-01&to=${month}-${lastDay}`,
    )
      .then((data) => {
        const byDate: Record<string, SlotEntry[]> = {};
        for (const slot of data.slots) (byDate[slot.date] ??= []).push(slot);
        state.monthCache[key] = byDate;
      })
      .catch((e: Error) => {
        state.monthCache[key] = {};
        state.errorMessage = e.message;
      })
      .finally(() => {
        delete monthInFlight[key];
        // Only the calendar shows month data; re-render if it's on screen.
        if (state.step === "date") render();
      }));
  }

  /** Load the month the calendar is showing. */
  function loadMonth() {
    const month = state.calendarMonth ?? ymdLocal(new Date()).slice(0, 7);
    return fetchMonth(state.service!.id, state.variant!.id, month);
  }

  /** Go to the calendar, opening on the month of the next free date. */
  function goToDate() {
    const next = state.variant ? state.nextAvailable?.[state.variant.id] : undefined;
    setState({
      step: "date",
      calendarMonth: next ? next.slice(0, 7) : undefined,
      dateYmd: undefined,
      slot: undefined,
      availableSlots: undefined,
    });
    loadMonth();
  }

  function timesChips(): string {
    if (!state.dateYmd) return `<div class="empty">Pick a date first.</div>`;
    const slots = state.availableSlots ?? [];
    if (slots.length === 0) return `<div class="empty">No times available on this day.</div>`;
    return slots
      .map(
        (s) => `
          <button type="button" class="chip" aria-pressed="${state.slot?.startsAt === s.startsAt}" data-slot="${s.startsAt}">
            ${escape(s.timeLocal)}
          </button>
        `,
      )
      .join("");
  }

  function isMultiSession(): boolean {
    return Boolean(state.service && state.service.serviceKind !== "ONE_OFF");
  }

  function sessionList(sessions: PublicService["variants"][number]["sessions"]): string {
    if (!sessions.length) return `<div class="cat">No sessions scheduled yet.</div>`;
    return `<ul style="margin:0;padding-left:18px;font-size:13px">${sessions
      .map((s) => {
        const d = new Date(s.startsAt);
        const weekday = d.toLocaleDateString("en-AU", { weekday: "short" });
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        const date = `${weekday} ${dd}/${mm}/${yyyy}`;
        const time = d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });
        return `<li>${escape(date)} · ${escape(time)}${s.label ? ` <span class="cat">— ${escape(s.label)}</span>` : ""}</li>`;
      })
      .join("")}</ul>`;
  }

  function extraRow(e: PublicService["extras"][number]): string {
    const qty = state.extras[e.id] ?? 0;
    return `
      <div class="extra">
        <div class="extra-name">
          ${escape(e.name)}
          <div class="extra-meta">
            ${fmtCents(e.priceCents)} · ${e.unit === "PER_HEAD" ? "per head" : "per booking"}
          </div>
        </div>
        <input type="number" class="qty" min="0" max="500" value="${qty}" data-extra="${e.id}" />
      </div>
    `;
  }

  /** Compose the structured waiver payload posted to /api/v1/bookings.
   * Only includes sections that this tenant has toggled on, so the server
   * doesn't get phantom empty fields for tenants that don't collect them. */
  function buildWaiverPayload() {
    const w = getWaiverConfig();
    const d = state.waiver;
    const payload: {
      signedName: string;
      completedByName?: string;
      signaturePngBase64?: string;
      participants?: WaiverParticipant[];
      address?: WaiverData["address"];
      emergencyContact?: WaiverData["emergencyContact"];
      medicalConditions?: string | null;
    } = { signedName: d.signedName };
    if (d.completedByName) payload.completedByName = d.completedByName;
    if (d.signaturePngBase64) payload.signaturePngBase64 = d.signaturePngBase64;
    if (w.collectParticipants) payload.participants = d.participants;
    if (w.collectAddress) payload.address = d.address;
    if (w.collectEmergencyContact) payload.emergencyContact = d.emergencyContact;
    if (w.collectMedical) {
      payload.medicalConditions =
        d.hasMedical === "yes" ? d.medicalConditions : null;
    }
    return payload;
  }

  function getWaiverConfig(): PublicWaiverConfig {
    return (
      state.tenant?.waiver ?? {
        enabled: false,
        body: "",
        collectParticipants: false,
        maxParticipants: 1,
        collectAddress: false,
        collectEmergencyContact: false,
        collectMedical: false,
      }
    );
  }

  function renderWaiverStep(): string {
    const w = getWaiverConfig();
    const d = state.waiver;
    return `
      ${backBtn("customer")}
      <h2>Waiver</h2>
      <p class="muted">Please read the waiver and complete each section below.</p>
      <div style="background:var(--w-bg-subtle);border:1px solid var(--w-border);border-radius:12px;padding:14px;max-height:240px;overflow:auto;font-size:13px;line-height:1.55;white-space:pre-wrap">${escape(w.body || "(No waiver body configured.)")}</div>

      ${w.collectParticipants ? renderParticipantsSection() : ""}
      ${w.collectAddress ? renderAddressSection() : ""}
      ${w.collectEmergencyContact ? renderEmergencyContactSection() : ""}
      ${w.collectMedical ? renderMedicalSection() : ""}

      <h3 style="margin:18px 0 8px">Sign &amp; agree</h3>
      <div class="col">
        <label>Full name of the adult completing this form</label>
        <input type="text" id="waiver-completed-by" value="${escape(d.completedByName)}" />
      </div>
      <div class="col" style="margin-top:8px">
        <label>Print your full name</label>
        <input type="text" id="waiver-name" value="${escape(d.signedName)}" />
      </div>
      <div class="col" style="margin-top:8px">
        <div style="display:flex;align-items:center;justify-content:space-between">
          <label>Sign here</label>
          <button type="button" class="back" id="sig-clear" style="margin:0;font-size:12px">Clear</button>
        </div>
        <div class="sig-wrap">
          <canvas id="sig-canvas" class="sig-canvas" width="900" height="280" aria-label="Signature pad"></canvas>
          <div class="sig-hint" id="sig-hint">Sign with your mouse or finger</div>
        </div>
      </div>
      <label style="margin-top:12px;display:flex;gap:8px;align-items:flex-start">
        <input type="checkbox" id="waiver-agree" ${d.agreed ? "checked" : ""} style="margin-top:3px" />
        <span style="font-size:13px;line-height:1.4">I, the above named, being the participant / parent or legal guardian of the participant(s), do hereby declare that all the information contained in this application is true and correct, and I have read and agree to the waiver above.</span>
      </label>
      ${state.errorMessage ? `<p class="err" style="margin-top:12px;padding:8px 12px;border-radius:8px;border:1px solid;font-size:13px">${escape(state.errorMessage)}</p>` : ""}
      <div class="footer">
        <button class="btn" id="next">Continue</button>
      </div>
    `;
  }

  function renderParticipantsSection(): string {
    const max = Math.max(1, state.tenant?.waiver.maxParticipants ?? 1);
    const ps = state.waiver.participants;
    return `
      <h3 style="margin:18px 0 8px">Participants</h3>
      <div id="participants">
        ${ps.map((p, i) => renderParticipantCard(p, i, ps.length > 1)).join("")}
      </div>
      ${
        ps.length < max
          ? `<button type="button" class="btn-ghost" id="add-participant" style="margin-top:8px">+ Add another participant</button>`
          : ""
      }
    `;
  }

  function renderParticipantCard(p: WaiverParticipant, i: number, canRemove: boolean): string {
    return `
      <div style="border:1px solid var(--w-border);border-radius:12px;padding:12px;margin-bottom:8px" data-participant="${i}">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
          <strong style="font-size:13px">Participant ${i + 1}</strong>
          ${canRemove ? `<button type="button" class="back" data-remove-participant="${i}" style="margin:0">Remove</button>` : ""}
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
          <div class="col">
            <label>First name</label>
            <input type="text" data-p-field="firstName" data-p-index="${i}" value="${escape(p.firstName)}" />
          </div>
          <div class="col">
            <label>Last name</label>
            <input type="text" data-p-field="lastName" data-p-index="${i}" value="${escape(p.lastName)}" />
          </div>
          <div class="col">
            <label>Date of birth</label>
            <input type="date" data-p-field="dob" data-p-index="${i}" value="${escape(p.dob)}" />
          </div>
          <div class="col">
            <label>Age</label>
            <input type="number" min="0" max="120" data-p-field="age" data-p-index="${i}" value="${escape(p.age)}" />
          </div>
          <div class="col" style="grid-column:1/-1">
            <label>Gender</label>
            <div style="display:flex;gap:14px;font-size:13px;padding-top:4px">
              <label style="display:flex;gap:6px;align-items:center;font-weight:400">
                <input type="radio" name="gender-${i}" data-p-field="gender" data-p-index="${i}" value="Male" ${p.gender === "Male" ? "checked" : ""} /> Male
              </label>
              <label style="display:flex;gap:6px;align-items:center;font-weight:400">
                <input type="radio" name="gender-${i}" data-p-field="gender" data-p-index="${i}" value="Female" ${p.gender === "Female" ? "checked" : ""} /> Female
              </label>
              <label style="display:flex;gap:6px;align-items:center;font-weight:400">
                <input type="radio" name="gender-${i}" data-p-field="gender" data-p-index="${i}" value="Other" ${p.gender === "Other" ? "checked" : ""} /> Other
              </label>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function renderAddressSection(): string {
    const a = state.waiver.address;
    return `
      <h3 style="margin:18px 0 8px">Address</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
        <div class="col" style="grid-column:1/-1">
          <label>Address line 1</label>
          <input type="text" id="addr-line1" value="${escape(a.line1)}" />
        </div>
        <div class="col" style="grid-column:1/-1">
          <label>Address line 2 (optional)</label>
          <input type="text" id="addr-line2" value="${escape(a.line2)}" />
        </div>
        <div class="col">
          <label>City / suburb</label>
          <input type="text" id="addr-city" value="${escape(a.city)}" />
        </div>
        <div class="col">
          <label>State</label>
          <input type="text" id="addr-state" value="${escape(a.state)}" />
        </div>
        <div class="col">
          <label>Postcode</label>
          <input type="text" inputmode="numeric" id="addr-postcode" value="${escape(a.postcode)}" />
        </div>
      </div>
    `;
  }

  function renderEmergencyContactSection(): string {
    const e = state.waiver.emergencyContact;
    return `
      <h3 style="margin:18px 0 8px">Emergency contact</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
        <div class="col">
          <label>Name</label>
          <input type="text" id="ec-name" value="${escape(e.name)}" />
        </div>
        <div class="col">
          <label>Relationship</label>
          <input type="text" id="ec-relationship" value="${escape(e.relationship)}" />
        </div>
        <div class="col" style="grid-column:1/-1">
          <label>Contact number</label>
          <input type="tel" id="ec-phone" value="${escape(e.phone)}" />
        </div>
      </div>
    `;
  }

  /** Live-bind every input in the rendered waiver step to state, then wire
   * the participant add/remove buttons and the Continue action. */
  function wireWaiverStep(): void {
    const w = getWaiverConfig();

    // Participant inputs (radio + text). Use data-attribute lookups so a
    // re-render after add/remove still reaches the right index.
    root.querySelectorAll<HTMLInputElement>("[data-p-field]").forEach((el) => {
      const i = Number(el.getAttribute("data-p-index"));
      const f = el.getAttribute("data-p-field") as keyof WaiverParticipant;
      const ev = el.type === "radio" ? "change" : "input";
      el.addEventListener(ev, () => {
        if (!state.waiver.participants[i]) return;
        state.waiver.participants[i] = { ...state.waiver.participants[i], [f]: el.value };
      });
    });

    root.querySelector("#add-participant")?.addEventListener("click", () => {
      const max = Math.max(1, w.maxParticipants);
      if (state.waiver.participants.length >= max) return;
      state.waiver.participants = [...state.waiver.participants, emptyParticipant()];
      setState({});
    });

    root.querySelectorAll<HTMLButtonElement>("[data-remove-participant]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const i = Number(btn.getAttribute("data-remove-participant"));
        state.waiver.participants = state.waiver.participants.filter((_, idx) => idx !== i);
        if (state.waiver.participants.length === 0) {
          state.waiver.participants = [emptyParticipant()];
        }
        setState({});
      });
    });

    // Address
    if (w.collectAddress) {
      const bind = (id: string, key: keyof WaiverData["address"]) =>
        root.querySelector<HTMLInputElement>(`#${id}`)?.addEventListener("input", (e) => {
          state.waiver.address = { ...state.waiver.address, [key]: (e.target as HTMLInputElement).value };
        });
      bind("addr-line1", "line1");
      bind("addr-line2", "line2");
      bind("addr-city", "city");
      bind("addr-state", "state");
      bind("addr-postcode", "postcode");
    }

    // Emergency contact
    if (w.collectEmergencyContact) {
      const bindEc = (id: string, key: keyof WaiverData["emergencyContact"]) =>
        root.querySelector<HTMLInputElement>(`#${id}`)?.addEventListener("input", (e) => {
          state.waiver.emergencyContact = {
            ...state.waiver.emergencyContact,
            [key]: (e.target as HTMLInputElement).value,
          };
        });
      bindEc("ec-name", "name");
      bindEc("ec-relationship", "relationship");
      bindEc("ec-phone", "phone");
    }

    // Medical — radio toggle + conditional textarea visibility (no full re-render).
    if (w.collectMedical) {
      root.querySelectorAll<HTMLInputElement>('input[name="hasMedical"]').forEach((el) => {
        el.addEventListener("change", () => {
          state.waiver.hasMedical = el.value as "yes" | "no";
          const wrap = root.querySelector<HTMLElement>("#med-details-wrap");
          if (wrap) wrap.style.display = state.waiver.hasMedical === "yes" ? "" : "none";
        });
      });
      root.querySelector<HTMLTextAreaElement>("#med-details")?.addEventListener("input", (e) => {
        state.waiver.medicalConditions = (e.target as HTMLTextAreaElement).value;
      });
    }

    // Sign / agree
    const completedBy = root.querySelector<HTMLInputElement>("#waiver-completed-by");
    const sig = root.querySelector<HTMLInputElement>("#waiver-name");
    const agree = root.querySelector<HTMLInputElement>("#waiver-agree");
    completedBy?.addEventListener("input", () => {
      state.waiver.completedByName = completedBy.value;
    });
    sig?.addEventListener("input", () => {
      state.waiver.signedName = sig.value;
    });
    agree?.addEventListener("change", () => {
      state.waiver.agreed = agree.checked;
    });
    wireSignaturePad();

    root.querySelector("#next")?.addEventListener("click", () => {
      // Read values once at submit time too so unfocused inputs without an
      // input event still flush through (e.g. autofill).
      if (completedBy) state.waiver.completedByName = completedBy.value.trim();
      if (sig) state.waiver.signedName = sig.value.trim();

      const err = validateWaiver();
      if (err) {
        state.errorMessage = err;
        setState({});
        return;
      }
      state.errorMessage = undefined;
      setState({ step: "review" });
    });
  }

  /** Wire up the signature canvas: pointer-event drawing, clear button,
   * "Sign with your mouse or finger" hint that fades out on first stroke,
   * and re-hydration of any previously-drawn signature on re-render. */
  function wireSignaturePad(): void {
    const canvas = root.querySelector<HTMLCanvasElement>("#sig-canvas");
    const hint = root.querySelector<HTMLDivElement>("#sig-hint");
    const clearBtn = root.querySelector<HTMLButtonElement>("#sig-clear");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Pin the internal coordinate space to the rendered CSS size × DPR so
    // strokes stay crisp on retina without ballooning the PNG payload.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle =
      state.tenant?.theme.mode === "dark" ? "#ffffff" : "#111111";

    // Re-hydrate any prior signature on re-render (e.g. after add-participant).
    if (state.waiver.signaturePngBase64) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        if (hint) hint.classList.add("sig-hint--hidden");
      };
      img.src = state.waiver.signaturePngBase64;
    }

    let drawing = false;
    let dirty = Boolean(state.waiver.signaturePngBase64);

    const pos = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    canvas.addEventListener("pointerdown", (e) => {
      drawing = true;
      canvas.setPointerCapture(e.pointerId);
      const { x, y } = pos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
      if (!dirty) {
        dirty = true;
        if (hint) hint.classList.add("sig-hint--hidden");
      }
    });
    canvas.addEventListener("pointermove", (e) => {
      if (!drawing) return;
      const { x, y } = pos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    });
    const endStroke = () => {
      if (!drawing) return;
      drawing = false;
      state.waiver.signaturePngBase64 = canvas.toDataURL("image/png");
    };
    canvas.addEventListener("pointerup", endStroke);
    canvas.addEventListener("pointercancel", endStroke);
    canvas.addEventListener("pointerleave", endStroke);

    clearBtn?.addEventListener("click", () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      state.waiver.signaturePngBase64 = "";
      dirty = false;
      if (hint) hint.classList.remove("sig-hint--hidden");
    });
  }

  function validateWaiver(): string | null {
    const w = getWaiverConfig();
    const d = state.waiver;
    if (w.collectParticipants) {
      for (const [i, p] of d.participants.entries()) {
        if (!p.firstName.trim() || !p.lastName.trim() || !p.dob || !p.age) {
          return `Please complete every field for participant ${i + 1}.`;
        }
        if (!p.gender) {
          return `Please choose a gender for participant ${i + 1}.`;
        }
      }
    }
    if (w.collectAddress) {
      const a = d.address;
      if (!a.line1.trim() || !a.city.trim() || !a.state.trim() || !a.postcode.trim()) {
        return "Please complete your address.";
      }
    }
    if (w.collectEmergencyContact) {
      const e = d.emergencyContact;
      if (!e.name.trim() || !e.relationship.trim() || !e.phone.trim()) {
        return "Please provide an emergency contact name, relationship and phone.";
      }
    }
    if (w.collectMedical) {
      if (d.hasMedical !== "yes" && d.hasMedical !== "no") {
        return "Please answer the medical conditions question.";
      }
      if (d.hasMedical === "yes" && !d.medicalConditions.trim()) {
        return "Please describe the medical conditions.";
      }
    }
    if (!d.completedByName.trim()) return "Please enter the name of the adult completing this form.";
    if (!d.signedName.trim()) return "Please print your full name.";
    if (!d.signaturePngBase64) return "Please sign in the signature box.";
    if (!d.agreed) return "Please acknowledge the declaration above.";
    return null;
  }

  function renderMedicalSection(): string {
    const has = state.waiver.hasMedical;
    return `
      <h3 style="margin:18px 0 8px">Medical information</h3>
      <div class="col">
        <label>Do the participant(s) suffer from any medical conditions?</label>
        <div style="display:flex;gap:14px;font-size:13px;padding-top:4px">
          <label style="display:flex;gap:6px;align-items:center;font-weight:400">
            <input type="radio" name="hasMedical" id="med-yes" value="yes" ${has === "yes" ? "checked" : ""} /> Yes
          </label>
          <label style="display:flex;gap:6px;align-items:center;font-weight:400">
            <input type="radio" name="hasMedical" id="med-no" value="no" ${has === "no" ? "checked" : ""} /> No
          </label>
        </div>
      </div>
      <div class="col" id="med-details-wrap" style="margin-top:8px;${has === "yes" ? "" : "display:none"}">
        <label>Please specify</label>
        <textarea id="med-details" rows="3">${escape(state.waiver.medicalConditions)}</textarea>
      </div>
    `;
  }

  function previewPrice() {
    const s = state.service!;
    const variant = state.variant;
    const baseUnit = variant?.priceCents ?? 0;
    const baseQty = 1;
    const lines: Array<{ label: string; lineTotalCents: number; quantity: number }> = [
      { label: variant?.label ?? s.name, quantity: baseQty, lineTotalCents: baseUnit * baseQty },
    ];
    for (const e of extrasForVariant()) {
      const q = state.extras[e.id] ?? 0;
      if (q > 0) {
        lines.push({
          label: `${e.name} × ${q}${e.unit === "PER_HEAD" ? " (per head)" : ""}`,
          quantity: q,
          lineTotalCents: e.priceCents * q,
        });
      }
    }
    const subtotal = lines.reduce((acc, l) => acc + l.lineTotalCents, 0);
    const inc = s.priceIncludesGst;
    const gst = inc ? subtotal - Math.round(subtotal / 1.1) : Math.round(subtotal * 0.1);
    const total = inc ? subtotal : subtotal + gst;
    return { lines, subtotalCents: subtotal, gstCents: gst, totalCents: total };
  }

  function attach() {
    root.querySelectorAll("[data-go]").forEach((b) =>
      b.addEventListener("click", () =>
        setState({ step: (b as HTMLElement).dataset.go as Step }),
      ),
    );

    if (state.step === "list") {
      root.querySelectorAll("button[data-service]").forEach((b) =>
        b.addEventListener("click", () => {
          const id = (b as HTMLButtonElement).dataset.service!;
          const service = state.services.find((s) => s.id === id);
          if (!service) return;
          setState({
            ...enterService(service),
            extras: {},
            slot: undefined,
            availableSlots: undefined,
            dateYmd: undefined,
          });
        }),
      );
    }

    if (state.step === "variant") {
      root.querySelectorAll("button[data-variant]").forEach((b) =>
        b.addEventListener("click", () => {
          const variantId = (b as HTMLButtonElement).dataset.variant!;
          const v = state.service!.variants.find((x) => x.id === variantId);
          if (!v || v.placesLeft === 0) return;
          setState({ variant: v, slot: undefined, availableSlots: undefined, dateYmd: undefined });
        }),
      );
      // Option cards: pick and go straight to the calendar.
      root.querySelectorAll<HTMLButtonElement>("button[data-variant-card]").forEach((b) =>
        b.addEventListener("click", () => {
          const v = state.service!.variants.find((x) => x.id === b.dataset.variantCard);
          if (!v) return;
          state.variant = v;
          goToDate();
        }),
      );
      if (!isMultiSession() && state.service) loadNextAvailable(state.service);
      // Multi-session services skip the date/time picker — variant IS schedule.
      root.querySelector("#next")?.addEventListener("click", () =>
        isMultiSession() ? setState({ step: "extras" }) : goToDate(),
      );
    }

    if (state.step === "date") {
      root.querySelectorAll<HTMLButtonElement>("button[data-nav]").forEach((b) =>
        b.addEventListener("click", () => {
          const dir = b.dataset.nav as "prev" | "next";
          const cur =
            state.calendarMonth ?? ymdLocal(new Date()).slice(0, 7);
          const [y, m] = cur.split("-").map(Number);
          const next = new Date(y, m - 1 + (dir === "next" ? 1 : -1), 1);
          setState({
            calendarMonth: `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`,
          });
          loadMonth();
        }),
      );
      root.querySelectorAll("button[data-date]").forEach((b) =>
        b.addEventListener("click", () => {
          const ymd = (b as HTMLButtonElement).dataset.date!;
          const month = state.calendarMonth ?? ymdLocal(new Date()).slice(0, 7);
          const cached = state.monthCache[`${state.variant!.id}|${month}`]?.[ymd];
          if (cached) {
            setState({ dateYmd: ymd, slot: undefined, availableSlots: cached });
          } else {
            setState({ dateYmd: ymd, slot: undefined, loadingAvailability: true });
            loadAvailability(ymd);
          }
        }),
      );
      root.querySelectorAll("button[data-slot]").forEach((b) =>
        b.addEventListener("click", () => {
          const startsAt = (b as HTMLButtonElement).dataset.slot!;
          const slot = state.availableSlots?.find((s) => s.startsAt === startsAt);
          if (slot) setState({ slot });
        }),
      );
      root.querySelector("#next")?.addEventListener("click", () => setState({ step: "extras" }));
    }

    if (state.step === "extras") {
      // Back from extras lands on variant when we skipped the date step.
      const back = root.querySelector("[data-go='date']");
      if (back && isMultiSession()) back.setAttribute("data-go", "variant");
      const partySize = root.querySelector("#partySize") as HTMLInputElement | null;
      // Typing "15" passes through "1", so only clamp to the minimum once
      // the field is left (and again on Continue).
      partySize?.addEventListener("input", () => {
        state.partySize = Math.max(1, parseInt(partySize.value, 10) || 1);
      });
      partySize?.addEventListener("change", () => {
        state.partySize = Math.max(minPartySize(), state.partySize);
        partySize.value = String(state.partySize);
      });
      root.querySelectorAll("input[data-extra]").forEach((el) => {
        const input = el as HTMLInputElement;
        input.addEventListener("input", () => {
          const id = input.dataset.extra!;
          const next = { ...state.extras };
          const qty = Math.max(0, parseInt(input.value, 10) || 0);
          if (qty > 0) next[id] = qty;
          else delete next[id];
          state.extras = next;
        });
      });
      root.querySelector("#next")?.addEventListener("click", () =>
        setState({ step: "customer", partySize: Math.max(minPartySize(), state.partySize) }),
      );
    }

    if (state.step === "customer") {
      const name = root.querySelector("#cust-name") as HTMLInputElement;
      const email = root.querySelector("#cust-email") as HTMLInputElement;
      const phone = root.querySelector("#cust-phone") as HTMLInputElement;
      const notes = root.querySelector("#notes") as HTMLTextAreaElement;
      root.querySelector("#next")?.addEventListener("click", () => {
        if (!name.value.trim() || !email.value.trim()) {
          name.focus();
          return;
        }
        state.customer = {
          name: name.value.trim(),
          email: email.value.trim(),
          phone: phone.value.trim(),
        };
        state.notes = notes.value;
        const howHeard = root.querySelector("#how-heard") as HTMLSelectElement | null;
        if (howHeard) state.howHeard = howHeard.value;
        setState({ step: state.service?.requiresWaiver ? "waiver" : "review" });
      });
    }

    if (state.step === "waiver") {
      wireWaiverStep();
    }

    if (state.step === "review") {
      root.querySelector("#submit")?.addEventListener("click", () => submit());
      root.querySelectorAll<HTMLInputElement>('input[name="payOption"]').forEach((el) =>
        el.addEventListener("change", () => setState({ payInFull: el.value === "full" })),
      );
    }

    if (state.step === "pay") {
      mountPayment();
      root.querySelector("#pay")?.addEventListener("click", () => confirmPay());
    }

    if (state.step === "error") {
      root.querySelector("#restart")?.addEventListener("click", () =>
        setState({
          step: "list",
          service: undefined,
          variant: undefined,
          slot: undefined,
          dateYmd: undefined,
          availableSlots: undefined,
          extras: {},
          waiver: emptyWaiver(),
          errorMessage: undefined,
          confirmation: undefined,
        }),
      );
    }
  }

  async function loadAvailability(dateYmd: string) {
    try {
      const data = await api<{ slots: SlotEntry[] }>(
        `/api/v1/services/${state.service!.id}/availability?variantId=${state.variant!.id}&from=${dateYmd}&to=${dateYmd}`,
      );
      setState({ availableSlots: data.slots, loadingAvailability: false });
    } catch (e) {
      const err = e as Error;
      setState({ availableSlots: [], loadingAvailability: false, errorMessage: err.message });
    }
  }

  async function submit() {
    setState({ step: "submitting", errorMessage: undefined });
    try {
      const body = {
        serviceId: state.service!.id,
        variantId: state.variant?.id,
        // ONE_OFF supplies a slotStart; multi-session services derive
        // attended sessions from the variant on the server side.
        slotStart: isMultiSession() ? undefined : state.slot!.startsAt,
        partySize: state.partySize,
        extras: extrasForVariant()
          .filter((e) => (state.extras[e.id] ?? 0) > 0)
          .map((e) => ({ extraId: e.id, quantity: state.extras[e.id] })),
        customer: state.customer,
        waiver: state.service?.requiresWaiver
          ? buildWaiverPayload()
          : undefined,
        notes: state.notes || undefined,
        howHeard: state.howHeard || undefined,
        payInFull: state.payInFull && canPayInFull() ? true : undefined,
      };
      const res = await api<{ bookingId: string; totalCents: number; expiresAt: string }>(
        "/api/v1/bookings",
        { method: "POST", body: JSON.stringify(body) },
      );
      const confirmation = {
        bookingId: res.bookingId,
        totalCents: res.totalCents,
        expiresAt: res.expiresAt,
      };
      // Try to set up payment. If Stripe isn't configured for this tenant we
      // gracefully land on the "done" step with a "configure-Stripe" note —
      // useful while keys aren't pasted yet.
      try {
        const pi = await api<
          | { provider: "STRIPE"; amountCents: number; stripe: { clientSecret: string; publishableKey: string } }
          | { provider: "SQUARE"; amountCents: number; square: { applicationId: string; locationId: string; environment: "SANDBOX" | "PRODUCTION"; idempotencyKey: string } }
        >(
          `/api/v1/bookings/${res.bookingId}/payment-session`,
          { method: "POST", body: "{}" },
        );
        const paymentState =
          pi.provider === "STRIPE"
            ? {
                provider: "STRIPE" as const,
                amountCents: pi.amountCents,
                clientSecret: pi.stripe.clientSecret,
                publishableKey: pi.stripe.publishableKey,
              }
            : {
                provider: "SQUARE" as const,
                amountCents: pi.amountCents,
                applicationId: pi.square.applicationId,
                locationId: pi.square.locationId,
                environment: pi.square.environment,
                idempotencyKey: pi.square.idempotencyKey,
              };
        setState({ step: "pay", confirmation, payment: paymentState });
      } catch (e) {
        const err = e as Error;
        setState({
          step: "done",
          confirmation,
          errorMessage: `Booking held but online payment isn't set up: ${err.message}`,
        });
      }
    } catch (e) {
      const err = e as Error;
      setState({ step: "review", errorMessage: err.message });
    }
  }

  let stripeJsLoading: Promise<StripeFactory> | null = null;
  let stripeJsInstance: StripeJsInstance | null = null;
  let stripeElements: ReturnType<StripeJsInstance["elements"]> | null = null;
  let squareCard: SquareCard | null = null;
  /** Booking the Square card field is attached for. */
  let squareCardFor: string | null = null;
  /** Booking whose payment form failed to load — don't retry on re-render,
   * or error → re-render → retry loops forever and freezes the page. */
  let paymentFailedFor: string | null = null;
  let lightPayMount: HTMLDivElement | null = null;

  /** Light-DOM element slotted into the payment view (see "ctms-payment"). */
  function lightPaymentMount(): HTMLDivElement {
    if (!lightPayMount) {
      lightPayMount = document.createElement("div");
      lightPayMount.slot = "ctms-payment";
      hostEl.appendChild(lightPayMount);
    }
    return lightPayMount;
  }

  function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(s);
    });
  }

  function loadStripeJs(): Promise<StripeFactory> {
    if (stripeJsLoading) return stripeJsLoading;
    if ((window as unknown as { Stripe?: StripeFactory }).Stripe) {
      stripeJsLoading = Promise.resolve(
        (window as unknown as { Stripe: StripeFactory }).Stripe,
      );
      return stripeJsLoading;
    }
    stripeJsLoading = new Promise<StripeFactory>((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://js.stripe.com/v3/";
      s.async = true;
      s.onload = () => {
        const g = (window as unknown as { Stripe?: StripeFactory }).Stripe;
        if (g) resolve(g);
        else reject(new Error("Stripe.js loaded but window.Stripe is missing"));
      };
      s.onerror = () => reject(new Error("Failed to load Stripe.js"));
      document.head.appendChild(s);
    });
    return stripeJsLoading;
  }

  async function mountPayment() {
    if (!state.payment) return;
    const bookingId = state.confirmation?.bookingId ?? "";
    if (paymentFailedFor === bookingId) return;
    if (state.payment.provider === "STRIPE") {
      const mount = root.querySelector("#payment-mount");
      if (!mount) return;
      return mountStripe(mount, state.payment);
    }
    // Square lives in light DOM, so re-renders don't disturb it: attach once
    // per booking.
    if (squareCardFor === bookingId) return;
    squareCardFor = bookingId;
    return mountSquare(lightPaymentMount(), state.payment);
  }

  async function mountStripe(
    mount: Element,
    p: Extract<NonNullable<State["payment"]>, { provider: "STRIPE" }>,
  ) {
    try {
      const StripeCtor = await loadStripeJs();
      stripeJsInstance = StripeCtor(p.publishableKey);
      stripeElements = stripeJsInstance.elements({ clientSecret: p.clientSecret });
      const paymentElement = stripeElements.create("payment", { layout: "tabs" });
      paymentElement.mount(mount);
    } catch (e) {
      paymentFailedFor = state.confirmation?.bookingId ?? "";
      setState({ errorMessage: `Couldn't load Stripe: ${(e as Error).message}` });
    }
  }

  async function mountSquare(
    mount: Element,
    p: Extract<NonNullable<State["payment"]>, { provider: "SQUARE" }>,
  ) {
    try {
      const src =
        p.environment === "PRODUCTION"
          ? "https://web.squarecdn.com/v1/square.js"
          : "https://sandbox.web.squarecdn.com/v1/square.js";
      const w = window as unknown as { Square?: SquareGlobal };
      if (!w.Square) await loadScript(src);
      if (!w.Square) throw new Error("Square.js loaded but window.Square is missing");
      if (squareCard) await squareCard.destroy().catch(() => {});
      squareCard = null;
      mount.innerHTML = "";
      const payments = w.Square.payments(p.applicationId, p.locationId);
      const card = await payments.card();
      await card.attach(mount);
      squareCard = card;
    } catch (e) {
      paymentFailedFor = state.confirmation?.bookingId ?? "";
      squareCardFor = null;
      setState({ errorMessage: `Couldn't load the card form: ${(e as Error).message}` });
    }
  }

  async function confirmPay() {
    if (!state.payment || !state.confirmation) {
      setState({ errorMessage: "Payment not ready — try again in a moment." });
      return;
    }
    if (state.payment.provider === "STRIPE") return confirmStripe();
    return confirmSquare();
  }

  async function confirmStripe() {
    if (!stripeJsInstance || !stripeElements) {
      setState({ errorMessage: "Stripe is still loading — try again in a moment." });
      return;
    }
    setState({ step: "paying", errorMessage: undefined });
    try {
      const result = await stripeJsInstance.confirmPayment({
        elements: stripeElements,
        redirect: "if_required",
      });
      if (result.error) {
        setState({ step: "pay", errorMessage: result.error.message ?? "Payment failed" });
        return;
      }
      // Stripe's webhook transitions the booking to CONFIRMED server-side.
      setState({ step: "done" });
    } catch (e) {
      setState({ step: "pay", errorMessage: (e as Error).message });
    }
  }

  async function confirmSquare() {
    if (!squareCard || state.payment?.provider !== "SQUARE") {
      setState({ errorMessage: "Square is still loading — try again in a moment." });
      return;
    }
    setState({ step: "paying", errorMessage: undefined });
    try {
      const result = await squareCard.tokenize();
      if (result.status !== "OK" || !result.token) {
        const msg = result.errors?.[0]?.message ?? "Card couldn't be tokenized";
        setState({ step: "pay", errorMessage: msg });
        return;
      }
      const confirmRes = await api<{ status: "CONFIRMED" | "PROCESSING"; bookingId: string }>(
        `/api/v1/bookings/${state.confirmation!.bookingId}/confirm-square`,
        {
          method: "POST",
          body: JSON.stringify({
            sourceId: result.token,
            idempotencyKey: state.payment.idempotencyKey,
          }),
        },
      );
      if (confirmRes.status === "CONFIRMED") {
        setState({ step: "done" });
      } else {
        // PROCESSING — Square's webhook will flip the booking. Show success.
        setState({ step: "done" });
      }
    } catch (e) {
      setState({ step: "pay", errorMessage: (e as Error).message });
    }
  }

  /** State for opening a service: first bookable option, and — for per-head
   * pricing (holiday programs) — one person rather than the party default,
   * so nobody is charged for ten children by accident. Services with a
   * minimum party size start at least at that minimum. */
  function enterService(service: PublicService): Partial<State> {
    const perHeadOne = service.pricingModel === "PER_HEAD" && !partySizeExplicit;
    return {
      service,
      variant: service.variants.find((v) => v.placesLeft !== 0),
      step: "variant",
      nextAvailable: undefined,
      nextAvailableFor: undefined,
      calendarMonth: undefined,
      partySize: Math.max(service.minPartySize ?? 1, perHeadOne ? 1 : initialPartySize),
    };
  }

  // Pages often carry several widget instances (header, party, holiday…);
  // share one catalogue request between them.
  const shared = window as unknown as { __ctmsCatalogue?: Record<string, Promise<PublicCatalogue>> };
  const catalogues = (shared.__ctmsCatalogue ??= {});
  (catalogues[apiBase] ??= api<PublicCatalogue>("/api/v1/services"))
    .then((shared) => {
      // The catalogue is shared between instances; filter a copy.
      const data: PublicCatalogue = excludedServiceIds.size
        ? { ...shared, services: shared.services.filter((s) => !excludedServiceIds.has(s.id)) }
        : shared;
      applyTheme(data.tenant.theme);
      // `data-service` jumps straight past the service list — a trial popup
      // shouldn't make someone pick "Free Trial Class" out of a menu. An
      // unknown id falls back to the list rather than showing nothing.
      const preselected = preselectServiceId
        ? data.services.find((s) => s.id === preselectServiceId)
        : undefined;
      if (preselectServiceId && !preselected) {
        console.warn(
          `[CTMS] data-service "${preselectServiceId}" not found in the catalogue; showing the full list`,
        );
      }
      if (preselected) {
        setState({
          tenant: data.tenant,
          services: data.services,
          ...enterService(preselected),
        });
        return;
      }
      setState({ tenant: data.tenant, services: data.services });
    })
    .catch((err: Error) => setState({ step: "error", errorMessage: err.message }));

  /** Apply tenant theme: set the data-theme attribute on the root, override
   * accent via inline style, and inject Google Fonts when the tenant has
   * specified custom display/body fonts. */
  function applyTheme(theme: PublicWidgetTheme): void {
    root.setAttribute("data-theme", theme.mode);
    const inlineParts: string[] = [];
    if (theme.accent) {
      inlineParts.push(`--w-brand: ${theme.accent}`);
      // Derive a faded shadow color from the accent so the hover box-shadow
      // tints the same hue. Append 4D ≈ 30% alpha.
      inlineParts.push(`--w-brand-shadow: ${hexToRgba(theme.accent, 0.3)}`);
    }
    if (theme.fontDisplay) inlineParts.push(`--w-font-display: "${theme.fontDisplay}", var(--w-font-body)`);
    if (theme.fontBody) inlineParts.push(`--w-font-body: "${theme.fontBody}", -apple-system, BlinkMacSystemFont, sans-serif`);
    if (inlineParts.length) root.style.cssText = inlineParts.join("; ");

    // Pull in the Google Fonts the tenant asked for. We append the link to
    // document.head (not the shadow root) so the @font-face declarations
    // register at the document font registry; fonts in that registry are
    // inherited into shadow roots, whereas links inside a shadow only define
    // fonts visible within that shadow on some browsers. Document-level is
    // the safer bet across engines.
    const families = [theme.fontDisplay, theme.fontBody]
      .filter((f): f is string => Boolean(f))
      .map((f) => `family=${f.replace(/ /g, "+")}:wght@400;500;600;700`);
    if (families.length) {
      const href = `https://fonts.googleapis.com/css2?${families.join("&")}&display=swap`;
      if (!document.head.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
      }
    }
  }

  function hexToRgba(hex: string, alpha: number): string {
    const v = hex.replace("#", "");
    const r = parseInt(v.length === 3 ? v[0] + v[0] : v.slice(0, 2), 16);
    const g = parseInt(v.length === 3 ? v[1] + v[1] : v.slice(2, 4), 16);
    const b = parseInt(v.length === 3 ? v[2] + v[2] : v.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return `rgba(204,0,0,${alpha})`;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  render();
})();
